/**
 * features/reports/AcademicWebView.tsx
 * ------------------------------------------------------------------
 * On-page HTML rendering of a student's academic report pages — the SAME
 * visual format as the printed report (two-tone accent, ACADEMIC RECORD,
 * subject cards with CA/Exam/Grade, teacher remarks, results summary), but
 * as native DOM. There is NO PDF and therefore NO print/download affordance:
 * teachers may VIEW live academic pages, never export them. Used by the
 * all-teacher Live view in View Reports.
 * ------------------------------------------------------------------
 */
import type { ReportStudent } from '@/lib/reportPdf';
import {
  getFormLevel,
  getPassMark,
  shouldShowGrades,
  termUsesExam,
  isAbsent,
  gradeColor,
} from '@/lib/grading';
import { termLabel } from '@/lib/school.config';

const NAVY = '#1b3a6b';
const GOLD = '#d4a92c';
const GOLDTINT = '#fbf3de';
const GOLDLINE = '#dec48c';
const RULE = '#dbe0e7';
const FAINT = '#f8fafd';
const INK = '#1e2634';
const RED = '#c13535';
const GREEN = '#168246';
const rgb = (c: [number, number, number]) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

function MarkCell({
  label,
  value,
  fail,
}: {
  label: string;
  value: number | 'ABS' | null;
  fail?: boolean;
}) {
  return (
    <div className="text-center min-w-[30px]">
      <div className="text-[9px] font-medium tracking-wide text-gray-400 uppercase">
        {label}
      </div>
      <div className="text-[15px] font-bold leading-tight" style={{ color: fail ? RED : INK }}>
        {value === null ? '–' : value}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <div className="text-[9px] font-medium tracking-wide text-gray-400 uppercase">
        {label}
      </div>
      <div className="text-[17px] font-bold leading-tight" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function RemarkBox({ title, text }: { title: string; text: string }) {
  return (
    <div
      className="rounded-lg border overflow-hidden mb-2"
      style={{ borderColor: RULE, borderTopColor: GOLDLINE }}
    >
      <div
        className="px-3 py-1.5 text-[12px] font-bold"
        style={{ background: GOLDTINT, color: NAVY, borderBottom: `1px solid ${GOLDLINE}` }}
      >
        {title}
      </div>
      <p className="px-3 py-2 text-[12px] leading-snug" style={{ color: INK }}>
        {text?.trim() || '—'}
      </p>
    </div>
  );
}

export default function AcademicWebView({
  student: s,
  examTitle,
}: {
  student: ReportStudent;
  examTitle?: string;
}) {
  const form = getFormLevel(s.className);
  const passMark = getPassMark(form);
  const showGrades = shouldShowGrades(form);
  const usesExam = s.juneCohort ? false : termUsesExam(s.term);
  const examHdr = (examTitle || 'Exam').toUpperCase();
  const termText = s.juneCohort ? 'Term 1' : termLabel(s.term);

  const gradeMarks = s.subjects
    .map((sub) => (usesExam ? (typeof sub.exam === 'number' ? sub.exam : null) : sub.termMark))
    .filter((m): m is number => typeof m === 'number');
  const avg = gradeMarks.length
    ? Math.round(gradeMarks.reduce((a, b) => a + b, 0) / gradeMarks.length)
    : 0;
  const failed = gradeMarks.filter((m) => m < passMark).length;
  const isT3 = s.term === 'Term3';
  const pass = failed < 2;

  return (
    <div className="mx-auto max-w-[640px] bg-white dark:bg-gray-900 rounded-xl shadow-sm ring-1 ring-black/5 overflow-hidden">
      {/* two-tone accent strip */}
      <div className="flex h-1.5">
        <div style={{ background: NAVY, width: '62%' }} />
        <div style={{ background: GOLD, width: '38%' }} />
      </div>

      <div className="px-5 pt-4 pb-5">
        {/* chrome header */}
        <div className="flex items-center justify-between">
          <span className="font-bold text-sm" style={{ color: NAVY }}>
            LKC
          </span>
          <span className="text-xs text-gray-500 truncate ml-3">
            {s.name} · {s.className}
          </span>
        </div>
        <div className="mt-1.5 border-b" style={{ borderColor: GOLD }} />

        {/* section title */}
        <div className="flex items-end justify-between mt-4 mb-3">
          <div className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-5 rounded-sm" style={{ background: NAVY }} />
            <h3 className="text-base font-bold tracking-wide" style={{ color: NAVY }}>
              ACADEMIC RECORD
            </h3>
          </div>
          <span className="text-xs text-gray-500">
            {termText} {s.year}
          </span>
        </div>

        {/* subjects */}
        <div className="space-y-2">
          {s.subjects.length === 0 && (
            <div
              className="text-sm text-gray-400 text-center py-6 rounded-lg border border-dashed"
              style={{ borderColor: RULE }}
            >
              No marks entered yet.
            </div>
          )}
          {s.subjects.map((sub, i) => {
            const examFail = usesExam && typeof sub.exam === 'number' && sub.exam < passMark;
            const markFail = !usesExam && typeof sub.termMark === 'number' && sub.termMark < passMark;
            const gc = sub.grade && sub.grade !== '-' ? gradeColor(sub.grade) : null;
            return (
              <div key={i} className="rounded-lg border overflow-hidden" style={{ borderColor: RULE }}>
                <div className="flex items-center gap-3 px-3 py-2" style={{ background: FAINT }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[13px] leading-tight" style={{ color: NAVY }}>
                      {sub.name}
                    </p>
                    {sub.teacher && (
                      <p className="text-[11px] text-gray-500 mt-0.5 truncate">{sub.teacher}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {usesExam ? (
                      <>
                        <MarkCell label="CA" value={sub.caMark} />
                        <MarkCell
                          label={examHdr}
                          value={sub.exam === null ? null : isAbsent(sub.exam) ? 'ABS' : (sub.exam as number)}
                          fail={examFail}
                        />
                      </>
                    ) : (
                      <MarkCell label="MARK" value={sub.termMark} fail={markFail} />
                    )}
                    {showGrades && (
                      <div className="text-center min-w-[30px]">
                        <div className="text-[9px] font-medium tracking-wide text-gray-400 uppercase">
                          Grade
                        </div>
                        {gc ? (
                          <div
                            className="mt-0.5 inline-flex items-center justify-center w-6 h-5 text-white text-[13px] font-bold"
                            style={{ background: rgb(gc) }}
                          >
                            {sub.grade}
                          </div>
                        ) : (
                          <div className="text-[15px] font-bold text-gray-400 leading-tight">–</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {sub.comment?.trim() && (
                  <p className="px-3 py-1.5 text-[12px] leading-snug" style={{ color: INK }}>
                    {sub.comment}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* teacher remarks */}
        <div className="flex items-center gap-2 mt-5 mb-2">
          <span className="inline-block w-1.5 h-5 rounded-sm" style={{ background: GOLD }} />
          <h3 className="text-sm font-bold tracking-wide" style={{ color: NAVY }}>
            TEACHER REMARKS
          </h3>
        </div>
        <RemarkBox
          title={`Class Teacher${s.classTeacherName ? ' · ' + s.classTeacherName : ''} — Attendance & Conduct`}
          text={s.classTeacherComment}
        />
        <RemarkBox
          title={`Head of Year${s.hodName ? ' · ' + s.hodName : ''} — on behalf of the Principal`}
          text={s.hodComment}
        />

        {/* results summary */}
        <div
          className="mt-4 flex items-center gap-6 rounded-lg px-4 py-3 relative overflow-hidden"
          style={{ background: FAINT }}
        >
          <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: NAVY }} />
          <Stat label="Average" value={`${avg}%`} color={INK} />
          <Stat label="Subjects failed" value={String(failed)} color={failed > 0 ? RED : GREEN} />
          {s.daysAbsent !== undefined && (
            <Stat label="Days absent" value={String(s.daysAbsent)} color={s.daysAbsent > 5 ? RED : INK} />
          )}
          {isT3 && (
            <span
              className="ml-auto px-3 py-1 rounded-md text-xs font-bold"
              style={{ background: pass ? '#dcf4e4' : '#fae0e0', color: pass ? GREEN : RED }}
            >
              {pass ? 'PASS' : 'FAIL'}
            </span>
          )}
        </div>
        <p className="text-[11px] text-gray-400 mt-2 text-right">
          Pass mark Form {form ?? '?'}: {passMark}%
        </p>
      </div>
    </div>
  );
}
